import { useTranslation } from 'react-i18next';
import { Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ArtworkUploadTarget } from '../hooks/useArtworkUploadLocation';

/**
 * The colour-mode upload target picker (BLU-838 V1 / decision D-35).
 *
 * Mirrors DESIGN.md §13.9's cross-org target picker rather than inventing a
 * control: REQUIRED + NO DEFAULT when the choice is ambiguous, amber trigger
 * plus helper text when unfilled (colour alone fails §16), shadcn Select at
 * this candidate count.
 *
 * It renders ONLY when the operator actually has a choice to make. A single
 * candidate resolves silently — the explainer line above already names it,
 * and a one-option dropdown is a decision the operator does not have. When it
 * does render with a resolved target (one owned floor among shared ones), the
 * trigger shows that resolved location, never the empty placeholder.
 *
 * Shared by colour mode and the Product updates focused view; pass a distinct
 * `id` when both can be on the page at once.
 */
export function ArtworkTargetPicker({
  target,
  id = 'colour-artwork-location',
}: {
  target: ArtworkUploadTarget;
  /** DOM id of the trigger; a second picker on the same page needs its own. */
  id?: string;
}) {
  const { t } = useTranslation('productSync');

  if (target.isLoading) {
    return <div className="h-9 w-full max-w-xs animate-pulse rounded-md bg-muted" aria-hidden="true" />;
  }
  // One resolved candidate and nothing to choose between — stay quiet.
  if (target.candidates.length <= 1 && !target.needsChoice) return null;

  const isMissing = !target.location;

  return (
    <div className="grid max-w-xs gap-1.5">
      <Label htmlFor={id} className="text-xs">
        {t('tier2.colourModeLocationLabel')}
        <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>
      </Label>
      <Select value={target.selectedId ?? target.location?.id ?? undefined} onValueChange={target.select}>
        <SelectTrigger
          id={id}
          aria-required
          aria-invalid={isMissing}
          aria-describedby={isMissing ? `${id}-helper` : undefined}
          className={cn(
            'h-9 text-xs transition-colors motion-reduce:transition-none',
            isMissing && 'border-amber-300 ring-1 ring-amber-100/50 focus:ring-amber-200',
          )}
        >
          <SelectValue placeholder={t('tier2.colourModeLocationPlaceholder')} />
        </SelectTrigger>
        <SelectContent>
          {target.candidates.map((candidate) => (
            <SelectItem key={candidate.id} value={candidate.id}>
              <span className="flex items-center gap-2">
                {candidate.isShared && (
                  <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
                )}
                <span>
                  {candidate.isShared
                    ? t('tier2.colourModeLocationShared', { name: candidate.name })
                    : t('tier2.colourModeLocationOwned', { name: candidate.name })}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isMissing && (
        <p id={`${id}-helper`} className="text-[11px] text-amber-700">
          {t('tier2.colourModeLocationHelper')}
        </p>
      )}
    </div>
  );
}
